import { computed } from 'vue'

import router from '@/router'
import { useAppStore, useBankStore, useCardStore, useProcessStore, useWebviewStore } from '@/store'

import { useI18n } from '@/hooks/useI18n'

import { showDialog, showConfirmDialog } from 'vant'

import { checkCardNo, checkUserInfo, checkUserPhoneCode } from '@/api/bindCard'

import type {
  CheckCardNoParams,
  CheckUserInfoParams,
  CheckUserInfoParamsAndCode,
} from '@/api/bindCard'

import Spinner from '@/components/Spinner'

import { BANK_MAP } from '@/model/card'
import { BANK_NO, PROCESS } from '@/constant'
interface BindCardConfig {
  startPage: string
  endPage?: string
  /** 流程待办 */
  todo?: string
  /** 是否跳过支付密码校验 */
  isSkipValidPwd?: boolean
  /** 流程参数 */
  params?: any
}

export default function useBindCard() {
  const config = computed(() => BANK_MAP.get(useBankStore().bankNo))
  const appStore = useAppStore()
  const cardStore = useCardStore()
  const webviewStore = useWebviewStore()
  const processStore = useProcessStore()

  const { t } = useI18n()

  /**
   * 绑卡流程
   * - 校验是否设置了支付密码
   *   - 有支付密码 进入到校验支付密码页面
   *   - 没有支付密码
   *     - 银联卡去第三方提供的绑卡界面(SDK)
   *     - 其他卡进入内部绑卡界面(直联)
   * @param config.startPage¸
   * @param config.todo¸
   */
  const toBindCard = (config: BindCardConfig) => {
    const bankStore = useBankStore()
    const processStore = useProcessStore()
    processStore.setPage(config.startPage, config.endPage, config.todo)
    processStore.process = PROCESS.BIND_CARD
    processStore.params = {
      ...config.params,
      bankNo: bankStore.bankNo,
    }

    cardStore.hasSetPwd().then((hadSetPwd) => {
      if (hadSetPwd) return router.push('confirmPwd')

      const bindCardPage = bankStore.bankNo === BANK_NO.UNION_PAY ? 'bindUnionCard' : 'addCard'
      router.push(bindCardPage)
    })
  }

  /** 校验绑卡的卡号/账户 */
  const checkBindCardCardNo = (form: CheckUserInfoParamsAndCode) => {
    const params: CheckCardNoParams = {
      bankNo: form.bankNo,
      cardNo: form.cardNo,
    }

    Spinner.loading()

    checkCardNo(params)
      .then((res) => {
        const body = res.result

        cardStore.$patch({
          cardName: body.cardName,
          bindingSessionId: body.bindingSessionId,
          icon: body.icon,
          icon4Detail: body.icon4Detail,
        })

        cardStore.setCardType()

        router.push('validCardInfo')
      })
      .finally(() => Spinner.close())
  }

  /** 校验绑卡的卡信息 */
  const checkBindCardInfo = (form: CheckUserInfoParamsAndCode) => {
    Spinner.loading()

    const params = validCardInfoParams(form)

    checkUserInfo(params)
      .then((res) => {
        switch (processStore.params.bankNo) {
          case BANK_NO.BOC_VM:
            const body = res.result
            cardStore.thirdDSOrderId = body.thirdDSOrderId
            cardStore.thirdDSRedirectHtml = body.thirdDSRedirectHtml
            cardStore.thirdDSState = body.thirdDSState

            if (webviewStore.isFromMiniProgram) {
              showDialog({
                title: t('common.tipsText'),
                message: t('common.toAppTips'),
                confirmButtonText: t('common.iSeeText'),
                showCancelButton: false,
              }).then(() => {
                const backLength = window.history.length - 1
                router.go(-backLength)
              })
              return
            }

            router.push('3ds')
            break

          case BANK_NO.BNU:
            Object.assign(processStore.params, params)
            router.push('findPwdValidCode')
            break
        }
      })
      .finally(() => Spinner.close())
  }

  /** 校验绑卡的手机号码验证码 */
  const checkBindCardCode = (code) => {
    const form: CheckUserInfoParamsAndCode = {
      ...(<CheckUserInfoParams>processStore.params),
      code,
    }

    Spinner.loading()

    checkUserPhoneCode(validCardInfoParams(form))
      .then(() => useBindCard().bindCardSuccess())
      .finally(() => Spinner.close())
  }

  /** 参数过滤 */
  const validCardInfoParams = (form): CheckUserInfoParamsAndCode => {
    const {
      bankNo,
      cardNo,
      name,
      birthday,
      expiryDate,
      cvn2,
      idType,
      idNo,
      phone,
      code,
      cardType,
      bindingSessionId,
      phoneArea,
    } = form

    return {
      bankNo,
      cardNo,
      name,
      birthday,
      expiryDate,
      cvn2,
      idType,
      idNo,
      phone,
      code,
      cardType,
      bindingSessionId,
      phoneArea,
    }
  }

  /** 绑卡成功后的操作 */
  const bindCardSuccess = () => {
    if (appStore.hadSetPwd) {
      cardStore.$reset()
      // 使用 processStore.historyLengthOnStart 计算返回步数
      // 这样可以准确地清除绑卡流程中产生的所有路由栈
      const historyLengthOnStart = processStore.historyLengthOnStart
      if (historyLengthOnStart > 0) {
        const backLength = window.history.length - historyLengthOnStart
        router.go(-backLength)
      } else {
        // 兜底逻辑：如果没有记录起始长度，使用 appStore.historyStackLength
        const backLength = window.history.length - appStore.historyStackLength
        router.go(-backLength)
      }
    } else {
      router.replace('setPwd')
    }
  }

  /** 放弃绑卡 */
  const interruptBindCard = () => {
    return new Promise((resolve, reject) => {
      showConfirmDialog({
        title: t('common.tipsText'),
        message: t('dialog.bindCard.message'),
        confirmButtonText: t('dialog.bindCard.confirmText'),
        cancelButtonText: t('dialog.bindCard.cancelText'),
      })
        .then(() => {
          resolve(null)
        })
        .catch(() => {
          reject()
        })
    })
  }

  return {
    config,
    toBindCard,
    bindCardSuccess,
    interruptBindCard,
    checkBindCardCardNo,
    checkBindCardInfo,
    checkBindCardCode,
    validCardInfoParams,
  }
}
